import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import { getTenantSlugHostError, sanitizeReturnTo } from "@repo/shared";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { SsoErrorAlert } from "@/components/auth/sso-error-alert";
import { WrongTenantOverlay } from "@/components/auth/wrong-tenant-overlay";
import { FullScreenLoader } from "@/components/full-screen-loader";
import { LegalFooter } from "@/components/legal-footer";
import { Card } from "@/components/ui/card";
import { getSessionTenantSlug, useSession } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { getPostLoginRedirect, isOAuthLogin, oauthLoginPath } from "@/lib/post-login-redirect";
import { getTenantHostContext, tenantUrl } from "@/lib/tenant-host";
import { DevLogin } from "./dev-login";
import { DevLoginLinks } from "./dev-login-links";
import { LoginForm } from "./login-form";
import { T } from "./page.i18n";

export default function LoginPage() {
  const t = useT(T);
  const [searchParams] = useSearchParams();
  const { data: session, isPending } = useSession();
  // QL-348 — a failed SSO login lands here with `?ssoError=<code>`, and (QL-637)
  // `?ssoKind=` (the API's closed-enum classification) and `?ssoRef=` (a
  // correlation reference an administrator can look up), and `?ssoMessage=` (the
  // identity provider's message, re-sanitised by the alert). Read once on mount:
  // the login form strips them from the URL so a reload does not re-show them.
  const [ssoError] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ssoError");
    return code
      ? {
          code,
          kind: params.get("ssoKind"),
          ref: params.get("ssoRef"),
          message: params.get("ssoMessage"),
        }
      : null;
  });

  // QL-343 — with tenant subdomains a signed-in visitor never needs this page on
  // the root domain (go to the session's tenant), and on another tenant's host
  // gets the wrong-organization overlay instead of a second sign-in.
  const hostContext = useMemo(getTenantHostContext, []);
  const subdomainsEnabled = hostContext.kind === "root" || hostContext.kind === "tenant";
  const sessionTenantSlug = getSessionTenantSlug(session);
  const hasTenantHint = searchParams.has("tenant");
  // An OAuth sign-in (MCP client, QP's own app) lands here with a session only when
  // it asked to sign in again (`prompt=login`) — e.g. to switch organization — so the
  // form below handles it, carrying the request along, instead of the dashboard.
  const oauthLogin = isOAuthLogin(searchParams);
  const sendToSessionTenant =
    hostContext.kind === "root" &&
    !hasTenantHint &&
    !oauthLogin &&
    sessionTenantSlug !== null &&
    getTenantSlugHostError(sessionTenantSlug) === null;

  useEffect(() => {
    if (sendToSessionTenant && sessionTenantSlug) {
      window.location.replace(tenantUrl(sessionTenantSlug, "/"));
    }
  }, [sendToSessionTenant, sessionTenantSlug]);

  if (subdomainsEnabled && (isPending || sendToSessionTenant)) return <FullScreenLoader />;

  if (
    hostContext.kind === "tenant" &&
    sessionTenantSlug !== null &&
    sessionTenantSlug !== hostContext.slug
  ) {
    return (
      <WrongTenantOverlay
        sessionTenantSlug={sessionTenantSlug}
        hostTenantSlug={hostContext.slug}
        returnTo={
          oauthLogin
            ? oauthLoginPath(searchParams)
            : (sanitizeReturnTo(searchParams.get("redirect"), window.location.origin) ?? "/")
        }
        // An OAuth sign-in can also finish as the organization already signed in.
        continueUrl={oauthLogin ? getPostLoginRedirect(searchParams) : undefined}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Suspense fallback={null}>
        <DevLogin />
      </Suspense>

      <div className="w-full max-w-2xl">
        <Card className="w-full max-w-md p-8 mx-auto">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-4">
              {/* Show light mode logo by default, dark mode logo when .dark class is present */}
              <img
                src={logoLight}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="dark:hidden"
              />
              <img
                src={logoDark}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="hidden dark:block"
              />
            </div>
            <h1 className="text-2xl font-bold">{t.welcome}</h1>
            <p className="text-muted-foreground mt-2">{t.signInToAccount}</p>
          </div>

          {ssoError && (
            <SsoErrorAlert
              code={ssoError.code}
              kind={ssoError.kind}
              reference={ssoError.ref}
              message={ssoError.message}
              className="mb-6"
            />
          )}
          <LoginForm />
        </Card>

        {process.env.NODE_ENV === "development" && (
          <div className="mt-6">
            <DevLoginLinks />
          </div>
        )}

        <LegalFooter />
      </div>
    </div>
  );
}
